import { useCallback, useEffect, useState } from 'react';

export interface AsyncState<T> {
  data?: T;
  error?: Error;
  loading: boolean;
  reload: () => void;
}

/**
 * Kleiner Daten-Lade-Hook: führt `fn` aus, verwaltet loading/error
 * und bietet `reload()`. `deps` steuert die Neuausführung.
 */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<Error>();
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  const memoFn = useCallback(fn, deps);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(undefined);
    memoFn()
      .then((d) => {
        if (active) {
          setData(d);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (active) {
          setError(e instanceof Error ? e : new Error(String(e)));
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [memoFn, tick]);

  return { data, error, loading, reload };
}
